import { Reducer } from "redux";
import { AuthState } from "./models";
import { HandledAction } from "modules/auth/actionCreator";

export type KnownAction = HandledAction;

export const reducer: Reducer<AuthState> = (state: AuthState, action: KnownAction) => {
    switch (action.type) {
        // Part of the auth state is populated server side,
        // so when the app bootstraps on the client,
        // we will make sure the login state is present
        case "@@INIT": {
            return {
                ...state,
                login: {
                    isFetching: false,
                    message: null,
                    secondaryMessage: null,
                    nonPilotUserLoggedIn: false,
                    artaLoginUrl: null,
                    isFirstStage2fa: false,
                    isVerifyingOtp: false,
                    isResendingOtp: false,
                    otpProfileError: false
                }
            };
        }
        case "LOG_IN_INIT": {
            return {
                ...state,
                login: {
                    ...state.login,
                    isFetching: true,
                    message: null,
                    secondaryMessage: null,
                    nonPilotUserLoggedIn: false,
                    artaLoginUrl: null,
                    isFirstStage2fa: false,
                    isVerifyingOtp: false,
                    otpProfileError: false
                }
            };
        }
        case "LOG_IN_FIRST_STAGE_2FA": {
            return {
                ...state,
                login: {
                    ...state.login,
                    isFetching: false,
                    message: null,
                    secondaryMessage: null,
                    nonPilotUserLoggedIn: false,
                    artaLoginUrl: null,
                    isFirstStage2fa: true,
                    isVerifyingOtp: false,
                    maskedEmail: action.authResponse.maskedEmail,
                    otpResendMessage: null,
                    otpProfileError: false,
                    username: action.username
                },
                session: {
                    ...state.session,
                    // Store the first-stage antiforgery token so the resend-OTP POST (which is now
                    // antiforgery-validated) sends a valid token before the OTP is verified.
                    antiforgeryToken: action.authResponse.antiforgeryToken,
                    isFirstStage2fa: true
                }
            };
        }
        case "VERIFY_OTP_INIT": {
            return {
                ...state,
                login: {
                    ...state.login,
                    isVerifyingOtp: true,
                    message: null,
                    otpResendMessage: null,
                    otpProfileError: false
                }
            };
        }
        case "VERIFY_OTP_COMPLETE": {
            return {
                ...state,
                login: {
                    ...state.login,
                    isVerifyingOtp: false,
                    isFirstStage2fa: action.authResponse.succeeded ? false : state.login.isFirstStage2fa,
                    message: action.authResponse.succeeded ? null : action.authResponse.message,
                    otpResendMessage: null
                }
            };
        }
        case "RESEND_OTP_INIT": {
            return {
                ...state,
                login: {
                    ...state.login,
                    isResendingOtp: true,
                    // Clear prior messages while the request is in flight; the outcome sets the next one.
                    message: null,
                    otpResendMessage: null
                }
            };
        }
        case "RESEND_OTP_COMPLETE": {
            return {
                ...state,
                login: {
                    ...state.login,
                    isResendingOtp: false,
                    // Clear any prior OTP error so the neutral confirmation isn't shown alongside it.
                    message: null,
                    otpResendMessage: action.message
                }
            };
        }
        case "RESEND_OTP_ERROR": {
            return {
                ...state,
                login: {
                    ...state.login,
                    isResendingOtp: false,
                    message: action.message,
                    otpResendMessage: null
                }
            };
        }
        case "LOG_IN_COMPLETE": {
            return {
                ...state,
                login: {
                    ...state.login,
                    isFetching: false,
                    message: action.authResponse.message,
                    secondaryMessage: action.authResponse.secondaryMessage,
                    nonPilotUserLoggedIn: action.nonPilotUserLoggedIn,
                    artaLoginUrl: action.authResponse.artaLoginUrl,
                    isFirstStage2fa: action.authResponse.succeeded ? false : state.login.isFirstStage2fa,
                    isVerifyingOtp: false,
                    otpProfileError: false,
                    maskedEmail: null
                },
                session: {
                    ...state.session,
                    userId: action.authResponse.userId,
                    accessTokenExpiry: action.authResponse.succeeded
                        ? action.authResponse.accessTokenExpiry
                        : state.session.accessTokenExpiry,
                    antiforgeryToken: action.authResponse.succeeded
                        ? action.authResponse.antiforgeryToken
                        : state.session.antiforgeryToken,
                    idle: false,
                    emulatedSession: action.authResponse.emulatedSession,
                    // Fully authenticated now — clear any first-stage flag seeded from the server on reload.
                    isFirstStage2fa: action.authResponse.succeeded ? false : state.session.isFirstStage2fa
                }
            };
        }
        case "REAUTHENTICATE_INIT": {
            return {
                ...state,
                login: {
                    ...state.login,
                    isFetching: true,
                    message: null,
                    secondaryMessage: null,
                    nonPilotUserLoggedIn: false,
                    artaLoginUrl: null
                }
            };
        }
        case "REAUTHENTICATE_COMPLETE": {
            return {
                ...state,
                login: {
                    ...state.login,
                    isFetching: false,
                    message: action.authResponse.message,
                    secondaryMessage: action.authResponse.secondaryMessage,
                    nonPilotUserLoggedIn: false,
                    artaLoginUrl: null
                },
                session: action.authResponse.succeeded
                    ? {
                          ...state.session,
                          userId: action.authResponse.userId,
                          accessTokenExpiry: action.authResponse.accessTokenExpiry,
                          antiforgeryToken: action.authResponse.antiforgeryToken,
                          idle: false
                      }
                    : state.session
            };
        }
        case "REFRESH_ACCESS_TOKEN_INIT": {
            return state;
        }
        case "REFRESH_ACCESS_TOKEN_COMPLETE": {
            if (action.authResponse.succeeded) {
                return {
                    ...state,
                    session: {
                        ...state.session,
                        userId: action.authResponse.userId,
                        accessTokenExpiry: action.authResponse.accessTokenExpiry,
                        antiforgeryToken: action.authResponse.antiforgeryToken
                    }
                };
            }
            return state;
        }
        case "SET_USER_IDLE": {
            return {
                ...state,
                session: {
                    ...state.session,
                    idle: action.idle
                }
            };
        }
        default:
            // The following line guarantees that every action in the KnownAction union has been covered by a case above
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const exhaustiveCheck: never = action;
    }

    return (
        state ||
        ({
            login: {
                isFetching: false,
                message: null,
                secondaryMessage: null,
                nonPilotUserLoggedIn: false,
                artaLoginUrl: null,
                isFirstStage2fa: false,
                isVerifyingOtp: false,
                isResendingOtp: false,
                maskedEmail: null
            },
            session: {
                userId: null,
                accessTokenExpiry: 0,
                inactiveReauthRequiredMs: 0,
                inactiveForcedLogoutMs: 0,
                idle: false,
                isFirstStage2fa: false
            }
        } as AuthState)
    );
};
